import React, { useState } from 'react';
import { Card } from '../ui/Card.tsx';
import { Button } from '../ui/Button.tsx';
import { Download, Shield, FileText, AlertCircle } from 'lucide-react';
import { exportUserData } from '../../services/accountService.ts';
import type { UserProfile } from '../../hooks/useUserProfile.ts';

interface DataPrivacySectionProps {
  profile: UserProfile;
  onSuccess?: (message: string) => void;
  onError?: (error: string) => void;
}

export const DataPrivacySection: React.FC<DataPrivacySectionProps> = ({ 
  profile, 
  onSuccess, 
  onError 
}) => {
  const [exportLoading, setExportLoading] = useState(false);

  console.log('[DataPrivacySection] Rendering with profile:', {
    hasUser: !!profile.user,
    loading: profile.loading,
  });

  const handleDataExport = async () => {
    if (!profile.user?.id) {
      onError?.('No user ID available for data export');
      return;
    }

    console.log('[DataPrivacySection] Starting data export for user:', profile.user.id);
    setExportLoading(true);
    
    try {
      await exportUserData();
      console.log('[DataPrivacySection] Data export completed successfully');
      onSuccess?.('Your data has been exported and downloaded successfully.');
    } catch (err) {
      console.error('[DataPrivacySection] Data export failed:', err);
      const errorMessage = err instanceof Error ? err.message : 'Failed to export data';
      onError?.(errorMessage);
    } finally {
      setExportLoading(false);
    }
  };

  if (profile.loading) {
    return (
      <Card className="p-6">
        <div className="h-6 bg-gray-200 rounded-md w-32 mb-4 animate-pulse"></div>
        <div className="space-y-3 mb-4">
          <div className="h-4 bg-gray-200 rounded-md w-full animate-pulse"></div>
          <div className="h-4 bg-gray-200 rounded-md w-3/4 animate-pulse"></div>
        </div>
        <div className="h-10 bg-gray-200 rounded-md animate-pulse"></div>
      </Card>
    );
  }

  if (!profile.user) {
    return (
      <Card className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <Shield className="h-5 w-5 mr-2" />
          Data & Privacy
        </h3>
        <p className="text-gray-500">Please log in to access data privacy controls.</p>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
        <Shield className="h-5 w-5 mr-2 text-purple-400" />
        Data & Privacy
      </h3>
      
      <div className="space-y-4">
        <div className="flex items-start space-x-3">
          <FileText className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="flex-1">
            <h4 className="text-sm font-medium text-gray-200">Download Your Data</h4>
            <p className="text-sm text-gray-400 mt-1">
              Export all your account data, including profile information, scans, and results in JSON format. 
              This includes everything we have stored about your account.
            </p>
          </div>
        </div>

        <div className="pl-8">
          <Button
            onClick={handleDataExport}
            disabled={exportLoading}
            variant="outline"
            className="flex items-center"
          >
            <Download className="h-4 w-4 mr-2" />
            {exportLoading ? 'Preparing Export...' : 'Download My Data'}
          </Button>
        </div>

        <div className="mt-6 p-4 bg-blue-900/30 border border-blue-700/50 rounded-lg">
          <div className="flex items-start space-x-3">
            <AlertCircle className="h-5 w-5 text-blue-400 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-blue-300">Your Privacy Rights</h4>
              <div className="text-sm text-blue-200 mt-1 space-y-1">
                <p>• You have the right to access and download your personal data</p>
                <p>• You can request corrections to inaccurate information</p>
                <p>• You can delete your account and all associated data at any time</p>
                <p>• We only collect data necessary for providing our security scanning service</p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4 text-xs text-gray-400">
          <p>
            Data export includes: account profile, subscription details, scan history, 
            and scan results. File contents and uploaded code are not included for security reasons.
          </p>
        </div>
      </div>
    </Card>
  );
}; 